import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { format } from "date-fns";
import toast from "react-hot-toast";
import MoreActionsMenu from "../common/MoreActionsMenu";

const Container = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const Article = styled.article`
  margin-bottom: 4rem;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 3rem;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`;

const Title = styled.h1`
  line-height: 1.1;
  flex: 1;
  text-align: center;
`;

const Spacer = styled.div`
  width: 40px; /* Same width as the more actions button */
`;

const Meta = styled.div`
  font-size: 1rem;
  color: var(--color-text-light);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Author = styled.span`
  font-weight: 500;
`;

const Category = styled.span`
  color: var(--color-accent);
  font-weight: 500;

  &:before {
    content: "•";
    margin-right: 1rem;
    color: var(--color-text-light);
  }
`;

const HeroImageContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 3rem auto;
  overflow: hidden;
  border-radius: 12px;
  background: var(--color-bg);
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
`;

const Content = styled.div`
  font-size: 1.125rem;
  line-height: 1.8;

  p {
    margin-bottom: 1.5rem;
  }

  h2 {
    margin-top: 3rem;
    margin-bottom: 1.5rem;
  }

  h3 {
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  ul,
  ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  blockquote {
    margin: 2rem 0;
    padding-left: 2rem;
    border-left: 4px solid var(--color-accent);
    font-style: italic;
    color: var(--color-text-light);
  }

  img {
    max-width: 100%;
    height: auto;
    margin: 2rem auto;
    display: block;
    border-radius: 8px;
  }

  a {
    color: var(--color-accent);
    text-decoration: underline;

    &:hover {
      color: var(--color-accent-hover);
    }
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: var(--color-text-light);
`;

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAuthorized } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const docRef = doc(db, "posts", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setPost({ id: docSnap.id, ...docSnap.data() });
        } else {
          toast.error("Post not found");
          navigate("/");
        }
      } catch (error) {
        console.error("Error fetching post:", error);
        toast.error("Failed to load post");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id, navigate]);

  if (loading) {
    return (
      <Container>
        <LoadingState>Loading post...</LoadingState>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container>
        <LoadingState>Post not found</LoadingState>
      </Container>
    );
  }

  const formattedDate = post.createdAt
    ? format(
        post.createdAt.toDate
          ? post.createdAt.toDate()
          : new Date(post.createdAt),
        "MMMM d, yyyy"
      )
    : "";

  // Allow any authorized admin to edit posts
  const canEdit = isAuthorized;

  // Get hero image URL (no transformations needed)
  const heroImageUrl = post.thumbnail || null;

  return (
    <Container>
      <Article>
        <Header>
          <TitleWrapper>
            {canEdit ? <Spacer /> : null}
            <Title>{post.title}</Title>
            {canEdit && (
              <MoreActionsMenu
                item={post}
                collection="posts"
                onEdit={() => navigate(`/admin/edit/${post.id}`)}
              />
            )}
          </TitleWrapper>
          <Meta>
            <time>{formattedDate}</time>
            {post.category && <Category>{post.category}</Category>}
            {post.author && <Author>by {post.author.displayName}</Author>}
          </Meta>
        </Header>

        {heroImageUrl && (
          <HeroImageContainer>
            <HeroImage src={heroImageUrl} alt={post.title} loading="eager" />
          </HeroImageContainer>
        )}

        <Content>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {post.content}
          </ReactMarkdown>
        </Content>
      </Article>
    </Container>
  );
};

export default BlogPost;
